/** BIM-vertailun tietotyypit.
 *
 *  Vastaa `vahti_ifc/compare.py`:n tulostetta ja on kopio katselimen
 *  `viewer/src/compliance.ts`:sta. Jos compare.py:n tuloste muuttuu, molemmat
 *  on paivitettava — kaksi kopiota on tietoinen valinta, koska projektit ovat
 *  erilliset eika PoC:iin haluta jaettua pakettia.
 *
 *  HUOM: katselimen `STATUKSET`-VAREJA ei tuoda tanne. Vahdin omissa
 *  nakymissa ei ole statusvareja eika poikkeamamittareita — mallitieto
 *  esitetaan faktoina (loytyyko tila, mika on mallin ala, montako vaatimusta
 *  loytyi). Varit jaavat katselimen ylatunnuksen laskureihin, jotka toimivat
 *  samalla suodattimena.
 */

export type BimStatus =
    | "ok"
    | "poikkeama"
    | "merkittava_poikkeama"
    | "ei_loydy"
    | "ei_tietoa"

export type NimikeStatus = "ok" | "liian_vahan" | "puuttuu" | "ei_arvioitavissa"

export interface BimKerros {
    globalId: string
    nimi: string
    korkeus: number
    tiloja: number
    elementit?: string[]
}

export interface BimPintaAla {
    vaadittu: number | null
    suunniteltu: number | null
    delta: number | null
    deltaPros: number | null
    status: BimStatus
}

export interface BimNimikeRivi {
    nimike: string
    kategoria: number | null
    vaadittuMaara: number
    loytyiMaara?: number
    mallissa?: string[]
    globalIdt?: string[]
    tasmennys?: string
    syy?: string
    ohje?: string
    ominaisuudet?: string[]
    status?: NimikeStatus
}

export interface BimVarusteet {
    status: BimStatus
    /** Kaikki tilan vaatimukset Vahdin omassa jarjestyksessa. */
    vaatimukset: BimNimikeRivi[]
    ylimaaraiset: { tyyppinimi: string | null; ifcLuokka: string; globalId: string }[]
}

export interface BimTila {
    globalId: string
    tilatunnus: string
    tilanimi: string | null
    status: BimStatus
    matchaus: {
        tapa: string | null
        luottamus: number
        vahtiTilatunnus?: string
        huonekorttiId?: number
        huomio?: string
    }
    pintaAla?: BimPintaAla
    varusteet?: BimVarusteet
    alanLahde?: string
    sisalto?: string[]
    pohja?: { polygoni: [number, number][]; z: number; korkeus?: number } | null
    kerros?: { globalId: string; nimi: string } | null
    kerrosLahde?: string | null
    vahti?: Record<string, string | number | null>
}

export interface BimCompliance {
    malli: {
        tiedosto: string
        skeema: string
        tilojenYlimaarainenKorkeus: number
        viitepisteet?: { globalId: string; keskipiste: [number, number, number] }[]
        kerrokset?: BimKerros[]
    }
    yhteenveto: {
        tilojaMallissa: number
        tilojaVahdissa: number
        matchattu: number
        statusJakauma: Partial<Record<BimStatus, number>>
        puuttuuMallista: number
        kynnykset: { okMaxPros: number; poikkeamaMaxPros: number; luottamusKynnys: number }
    }
    tilat: BimTila[]
    vainVahdissa: {
        tilatunnus: string
        toiminta: string | null
        tilanimike?: string | null
        vaadittuAla: number | null
    }[]
    /** bim_api.py lisaa: mita mallia vasten vertailtiin. */
    hanke?: {
        hankeId: number | null
        malli: string
        ifc: string
        /** true = kaytettiin oletusmallia, ei tälle hankkeelle maariteltya. */
        oletusmalli: boolean
    }
}

/** mallit.json:in rivi: mille hankkeelle IFC-malli on olemassa. */
export interface BimMalli {
    hankeId: number | null
    oletus?: boolean
    nimi: string
    ifc: string
    spaces: string
}

/** Suomalainen lukumuotoilu. Sama kuin katselimessa, jotta sama luku ei nayta
 *  eri muotoiselta Vahdissa ja mallissa. */
export const bimLuku = (n: number, desimaalit = 1) =>
    n.toLocaleString("fi-FI", {
        minimumFractionDigits: desimaalit,
        maximumFractionDigits: desimaalit,
    })
